import { useEffect, useRef, useState, type ReactNode } from "react";

import { ErrorAlert, Form, Modal, Field, Input, CONTROL_LG, Button } from "@/components/ui";
import { DraftOffer, DraftSavedNote } from "@/components/ui/FormDraft";
import { CONTACT_MAX, joinPhone, splitPhone, type Contact, type ContactInput } from "@/lib/domains";
import { useFormDraft } from "@/lib/form-drafts";
import { COUNTRIES, countryName } from "@/lib/payments";
import { useUnsavedChanges } from "@/lib/unsavedChanges";

type Values = Omit<ContactInput, "phone" | "organization_name" | "state"> & {
  organization_name: string;
  state: string;
  dial: string;
  number: string;
  label: string;
};

const EMPTY: Values = {
  first_name: "",
  last_name: "",
  organization_name: "",
  email: "",
  dial: "44",
  number: "",
  street: "",
  city: "",
  state: "",
  postcode: "",
  country: "GB",
  label: "",
};

function toForm(c: Contact | null | undefined): Values {
  if (!c) return EMPTY;
  const { dial, number } = splitPhone(c.phone);
  return {
    first_name: c.first_name,
    last_name: c.last_name,
    organization_name: c.organization_name ?? "",
    email: c.email,
    dial,
    number,
    street: c.street,
    city: c.city,
    state: c.state ?? "",
    postcode: c.postcode,
    country: c.country,
    label: c.label ?? "",
  };
}

/** One draft per new contact and one per saved contact, so a draft never lands on another contact. */
export const draftKeyOf = (c: Contact | null | undefined) => `domains.contact:${c?.id ?? "new"}`;

/** The form's values as the API takes them. */
export interface ContactFormValues extends ContactInput {
  label: string | null;
}

function toBody(f: Values): ContactFormValues {
  return {
    first_name: f.first_name.trim(),
    last_name: f.last_name.trim(),
    organization_name: f.organization_name.trim() || null,
    email: f.email.trim(),
    phone: joinPhone(f.dial, f.number),
    street: f.street.trim(),
    city: f.city.trim(),
    state: f.state.trim() || null,
    postcode: f.postcode.trim(),
    country: f.country,
    label: f.label.trim() || null,
  };
}

const REQUIRED: (keyof Values)[] = [
  "first_name",
  "last_name",
  "email",
  "number",
  "street",
  "city",
  "postcode",
  "country",
];

/**
 * A registration contact's details: adding a contact to the contact book, or changing one. `onSubmit` gets the
 * values; while it runs the button spins, and its error (field errors included) is shown in the form.
 *
 * PHP-3874: a half-filled form is kept as a draft (one per new contact, one per contact being changed) and offered
 * back with "Continue where you left off?". It is forgotten once `onSubmit` finishes without an error, unless
 * `clearDraftOnSubmit` is false (a form whose submit is only a preview: the caller forgets it once saved).
 */
export function ContactFormModal({
  open,
  onClose,
  title,
  subtitle,
  initial,
  submitLabel,
  onSubmit,
  pending,
  error,
  notice,
  clearDraftOnSubmit = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  initial?: Contact | null;
  submitLabel: string;
  onSubmit: (values: ContactFormValues) => void;
  pending: boolean;
  error: unknown;
  /** Shown above the buttons, e.g. what saving will change. */
  notice?: ReactNode;
  clearDraftOnSubmit?: boolean;
}) {
  // What the form opened with: the contact may be refreshed behind the dialog without that being an edit.
  const [start] = useState(() => toForm(initial));
  const [form, setForm] = useState<Values>(start);
  const set = <K extends keyof Values>(key: K, value: Values[K]) => setForm((f) => ({ ...f, [key]: value }));
  const complete = REQUIRED.every((k) => form[k].trim());
  const countries = COUNTRIES.includes(form.country) ? COUNTRIES : [form.country, ...COUNTRIES];
  const edited = (Object.keys(form) as (keyof Values)[]).some((k) => form[k] !== start[k]);
  const unsaved = useUnsavedChanges(open && edited && !pending, { ignoreSearch: true });
  const close = () => unsaved.guard(onClose);

  const draft = useFormDraft(draftKeyOf(initial), form, { ready: open });
  const wasPending = useRef(false);
  useEffect(() => {
    if (pending) wasPending.current = true;
    else if (wasPending.current) {
      wasPending.current = false;
      if (!error && clearDraftOnSubmit) draft.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to a submit finishing, nothing else
  }, [pending]);

  const field = (
    key: keyof Values,
    label: string,
    opts: { optional?: boolean; type?: string; auto?: string } = {},
  ) => (
    <Field size="lg" label={label} optional={opts.optional} name={key} required={REQUIRED.includes(key)}>
      {(control) => (
        <Input
          size="lg"
          {...control}
          type={opts.type ?? "text"}
          autoComplete={opts.auto}
          value={form[key]}
          maxLength={
            key in CONTACT_MAX
              ? CONTACT_MAX[key as keyof typeof CONTACT_MAX]
              : key === "label"
                ? 60
                : undefined
          }
          onChange={(e) => set(key, e.target.value)}
        />
      )}
    </Field>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={title}
        subtitle={subtitle}
        width={540}
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={close}>
              Cancel
            </Button>
            <Button variant="brand" size="lg" type="submit" form="contact-form" loading={pending}>
              {submitLabel}
            </Button>
          </>
        }
      >
        <Form
          id="contact-form"
          className="flex flex-col gap-4"
          error={error}
          onSubmit={(e) => {
            e.preventDefault();
            if (complete) onSubmit(toBody(form));
          }}
        >
          <DraftOffer draft={draft} onContinue={(saved) => setForm({ ...EMPTY, ...saved })} />
          {field("label", "Name in your contacts", { optional: true })}
          <div className="flex flex-col gap-3 sm:flex-row">
            {field("first_name", "First name", { auto: "given-name" })}
            {field("last_name", "Last name", { auto: "family-name" })}
          </div>
          {field("organization_name", "Organization", { optional: true, auto: "organization" })}
          <div className="flex flex-col gap-3 sm:flex-row">
            {field("email", "Email", { type: "email", auto: "email" })}
            <Field size="lg" label="Phone" name="phone" required>
              {(control) => (
                <div className="flex gap-2">
                  <div className="relative w-[78px] shrink-0">
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-fg-subtle">
                      +
                    </span>
                    <input
                      aria-label="Country calling code"
                      inputMode="numeric"
                      value={form.dial}
                      onChange={(e) => set("dial", e.target.value.replace(/\D/g, "").slice(0, 3))}
                      className={`${CONTROL_LG} pl-6`}
                    />
                  </div>
                  <Input
                    size="lg"
                    {...control}
                    type="tel"
                    autoComplete="tel-national"
                    className="min-w-0 flex-1"
                    value={form.number}
                    onChange={(e) => set("number", e.target.value)}
                  />
                </div>
              )}
            </Field>
          </div>
          {field("street", "Street address", { auto: "street-address" })}
          <div className="flex flex-col gap-3 sm:flex-row">
            {field("city", "City", { auto: "address-level2" })}
            {field("state", "State / county", { optional: true, auto: "address-level1" })}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            {field("postcode", "Postcode", { auto: "postal-code" })}
            <Field size="lg" label="Country" name="country" required>
              {(control) => (
                <select
                  {...control}
                  value={form.country}
                  autoComplete="country"
                  onChange={(e) => set("country", e.target.value)}
                  className={CONTROL_LG}
                >
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {countryName(c)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          {notice}
          <DraftSavedNote savedAt={draft.savedAt} />
          <ErrorAlert error={error} />
          {/* Hidden Enter-key submit trigger, not a visible action — not the shared Button. */}
          {/* eslint-disable-next-line portal/no-raw-button */}
          <button type="submit" hidden />
        </Form>
      </Modal>
      {unsaved.modal}
    </>
  );
}
